import { computed } from "vue";
import { useStore } from "vuex";
import { ProfileService } from "@/services/profile.service";
import { resolveProfileHashId } from "@/utils/resolveProfileHashId";

/**
 * Payload from GET /profiles/form-data (dealer: /v1/dealer/profiles/form-data).
 *
 * @typedef {object} ProfileFormDataResponse
 * @property {object} data
 * @property {object} data.user - UserResource (profile + addresses when loaded via form-data)
 * @property {string} data.user.id - User hashid → use for profile.user_id, PUT /users/{id}
 * @property {number} data.user.plainId - Numeric id; do not use in hashid URL segments
 * @property {object} data.user.profile - ProfileResource
 * @property {string} data.user.profile.id - Profile hashid → storeProfilePercentage profile_id
 * @property {number} data.user.profile.plainId - Numeric profile id; internal only
 * @property {Array<object>} data.user.addresses
 * @property {unknown} data.parent_attributes
 * @property {{ allWholesalers: Array<{id: number, name: string}>, wholesalers: unknown }=} data.wholesalers
 */

/**
 * Own-profile screen: load once via form-data, never GET /profiles/{hashId} for "my" profile.
 *
 * @param {import('vuex').Store} [store] - Optional store instance (e.g. outside setup)
 */
export function useProfileFormData(store = null) {
  const vuexStore = store ?? useStore();

  const me = computed(() => vuexStore.getters.getMe);
  const profile = computed(() => vuexStore.getters.getProfile);
  const addresses = computed(() => vuexStore.getters.getUserAddresses ?? []);

  /** User hashid — PUT profile.user_id, POST profiles/percentage user_id */
  const userHashId = computed(() => me.value?.id ?? null);
  /** Profile hashid — POST profiles/percentage profile_id */
  const profileHashId = computed(() =>
    resolveProfileHashId(profile.value, me.value),
  );
  /** Numeric user id — e.g. profiles/full-data-points/{plainId} only where API expects numeric id */
  const userPlainId = computed(() => me.value?.plainId ?? null);

  /**
   * @param {{ includeWholesalers?: boolean }} [options]
   * @returns {Promise<import('axios').AxiosResponse|null>}
   */
  /**
   * GET /profiles/form-data → hydrates Vuex (user, profile, addresses, parent_attributes, wholesalers).
   */
  async function loadProfileForm(options = {}) {
    return vuexStore.dispatch("loadProfileFormData", {
      includeWholesalers: options.includeWholesalers !== false,
    });
  }

  /**
   * Raw payload from API (after loadProfileForm). Prefer store getters in components.
   */
  async function fetchMyProfileForm(options = {}) {
    return ProfileService.fetchMyProfileForm({
      includeWholesalers: options.includeWholesalers !== false,
    });
  }

  /**
   * PUT /profiles/initial-data/update — profile.user_id must be userHashId (user.id).
   */
  async function saveMyProfile(profileFields) {
    const hashId = userHashId.value;
    if (!hashId) {
      return null;
    }
    return ProfileService.saveMyProfile(profileFields, hashId);
  }

  return {
    me,
    profile,
    addresses,
    userHashId,
    profileHashId,
    userPlainId,
    loadProfileForm,
    fetchMyProfileForm,
    saveMyProfile,
  };
}
